/**
 * Page de gestion des partenaires sociaux (ADMIN UNIQUEMENT)
 * CRUD complet : Créer, Modifier, Supprimer
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { partenaireService } from '../services/api';
import { PlusIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { notify } from '../components/NotificationContainer';
import { confirm } from '../components/ConfirmContainer';
import { useAuth } from '../contexts/AuthContext';
import logger from '../utils/logger';

const GestionPartenaires = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [partenaires, setPartenaires] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPartenaire, setEditingPartenaire] = useState(null);
  const [formData, setFormData] = useState({
    nom: '',
    description: '',
    couleur: '#3B82F6',
    actif: true,
  });

  const COULEURS_DISPONIBLES = [
    { value: '#3B82F6', label: 'Bleu' },
    { value: '#10B981', label: 'Vert' },
    { value: '#F59E0B', label: 'Orange' },
    { value: '#EF4444', label: 'Rouge' },
    { value: '#8B5CF6', label: 'Violet' },
    { value: '#EC4899', label: 'Rose' },
    { value: '#14B8A6', label: 'Cyan' },
    { value: '#F97316', label: 'Orange foncé' },
    { value: '#6366F1', label: 'Indigo' },
    { value: '#84CC16', label: 'Vert citron' },
  ];

  useEffect(() => {
    if (!user || user.role !== 'ADMIN') {
      navigate('/');
      return;
    }
    loadPartenaires();
  }, [user, navigate]);

  const loadPartenaires = async () => {
    try {
      setLoading(true);
      const response = await partenaireService.getAll();
      // L'API peut retourner soit un tableau direct, soit un objet avec results
      const data = Array.isArray(response.data) ? response.data : (response.data.results || []);
      setPartenaires(data);
    } catch (error) {
      logger.error('Erreur lors du chargement:', error);
      notify.error('Erreur lors du chargement des partenaires');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (partenaire = null) => {
    if (partenaire) {
      setEditingPartenaire(partenaire);
      setFormData({
        nom: partenaire.nom || '',
        description: partenaire.description || '',
        couleur: partenaire.couleur || '#3B82F6',
        actif: partenaire.actif !== undefined ? partenaire.actif : true,
      });
    } else {
      setEditingPartenaire(null);
      setFormData({
        nom: '',
        description: '',
        couleur: '#3B82F6',
        actif: true,
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingPartenaire(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingPartenaire) {
        await partenaireService.update(editingPartenaire.id, formData);
        notify.success('Partenaire modifié avec succès');
      } else {
        await partenaireService.create(formData);
        notify.success('Partenaire créé avec succès');
      }

      await loadPartenaires();
      handleCloseModal();
    } catch (error) {
      logger.error('Erreur lors de l\'enregistrement:', error);
      const errorMsg = error.response?.data?.nom?.[0] || 'Erreur lors de l\'enregistrement';
      notify.error(errorMsg);
    }
  };

  const handleDelete = async (id) => {
    if (await confirm.delete('Êtes-vous sûr de vouloir supprimer ce partenaire ?', 'Supprimer le partenaire')) {
      try {
        await partenaireService.delete(id);
        notify.success('Partenaire supprimé avec succès');
        await loadPartenaires();
      } catch (error) {
        logger.error('Erreur lors de la suppression:', error);
        notify.error('Erreur lors de la suppression du partenaire');
      }
    }
  };

  const handleChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData({
      ...formData,
      [e.target.name]: value,
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="w-full min-h-screen">
      <div className="max-w-7xl mx-auto py-4 sm:py-6 px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-4 sm:mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Gestion des Partenaires</h1>
            <p className="mt-1 text-xs sm:text-sm text-gray-600">Gérez les partenaires sociaux autorisés</p>
          </div>
          <button
            onClick={() => handleOpenModal()}
            className="flex items-center justify-center gap-2 bg-primary-600 hover:bg-primary-700 active:bg-primary-800 text-white py-2 px-4 rounded-md transition-colors text-sm sm:text-base whitespace-nowrap"
          >
            <PlusIcon className="h-4 w-4 sm:h-5 sm:w-5" />
            Créer un partenaire
          </button>
        </div>

        {partenaires.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 text-center">
            <p className="text-sm sm:text-base text-gray-500">Aucun partenaire créé</p>
            <button
              onClick={() => handleOpenModal()}
              className="mt-4 text-xs sm:text-sm text-primary-600 hover:text-primary-700 active:text-primary-800 transition-colors"
            >
              Créer le premier partenaire
            </button>
          </div>
        ) : (
          <div className="bg-white shadow-sm border border-gray-200 overflow-hidden rounded-md">
            <ul className="divide-y divide-gray-200">
              {partenaires.map((partenaire) => (
                <li key={partenaire.id} className="hover:bg-gray-50 active:bg-gray-100 transition-colors">
                  <div className="px-3 py-3 sm:px-4 sm:py-4 lg:px-6">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
                          <div
                            className="w-6 h-6 rounded flex-shrink-0"
                            style={{ backgroundColor: partenaire.couleur || '#3B82F6' }}
                            title="Couleur d'identification"
                          ></div>
                          <h3 className="text-base sm:text-lg font-medium text-gray-900 truncate">{partenaire.nom}</h3>
                          {partenaire.actif ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              Actif
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                              Inactif
                            </span>
                          )}
                        </div>
                        {partenaire.description && (
                          <p className="mt-1 text-sm text-gray-500">{partenaire.description}</p>
                        )}
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleOpenModal(partenaire)}
                          className="flex items-center gap-1 text-primary-600 hover:text-primary-900 transition-colors"
                        >
                          <PencilIcon className="h-5 w-5" />
                          <span className="text-sm">Modifier</span>
                        </button>
                        <button
                          onClick={() => handleDelete(partenaire.id)}
                          className="flex items-center gap-1 text-red-600 hover:text-red-900 transition-colors"
                        >
                          <TrashIcon className="h-5 w-5" />
                          <span className="text-sm">Supprimer</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Modal de création/édition */}
        {showModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-full max-w-lg shadow-lg rounded-md bg-white">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  {editingPartenaire ? 'Modifier le partenaire' : 'Créer un partenaire'}
                </h3>
                <button
                  onClick={handleCloseModal}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <span className="text-2xl">&times;</span>
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Nom du partenaire *</label>
                  <input
                    type="text"
                    name="nom"
                    required
                    value={formData.nom}
                    onChange={handleChange}
                    placeholder="Ex: CAF, MSA, Centre Social..."
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Description</label>
                  <textarea
                    name="description"
                    rows="3"
                    value={formData.description || ''}
                    onChange={handleChange}
                    placeholder="Description du partenaire (optionnel)..."
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Couleur d'identification *
                  </label>
                  <div className="grid grid-cols-5 gap-2">
                    {COULEURS_DISPONIBLES.map((couleur) => (
                      <button
                        key={couleur.value}
                        type="button"
                        onClick={() => setFormData({ ...formData, couleur: couleur.value })}
                        className={`relative h-10 rounded-md border-2 transition-all ${
                          formData.couleur === couleur.value
                            ? 'border-gray-900 ring-2 ring-offset-2 ring-gray-900'
                            : 'border-gray-300 hover:border-gray-400'
                        }`}
                        style={{ backgroundColor: couleur.value }}
                        title={couleur.label}
                      >
                        {formData.couleur === couleur.value && (
                          <span className="absolute inset-0 flex items-center justify-center text-white font-bold">
                            ✓
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    Cette couleur sera utilisée pour identifier visuellement le partenaire dans le tableau de bord
                  </p>
                </div>

                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="actif"
                      checked={formData.actif}
                      onChange={handleChange}
                      className="rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
                    />
                    <span className="ml-2 text-sm text-gray-700">Partenaire actif</span>
                  </label>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700"
                  >
                    {editingPartenaire ? 'Mettre à jour' : 'Créer'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GestionPartenaires;
